import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ShoppingCart } from 'lucide-react';
import { db, ensureDbOpen } from '../db/database';

const OrdersReversed = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [productsById, setProductsById] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[OrdersReversed] Mounting component...');
    const load = async () => {
      try {
        setIsLoading(true);
        await ensureDbOpen();
        console.log('[OrdersReversed] DB opened, loading ordersReversed and products...');

        const [reversedData, products] = await Promise.all([
          db.ordersReversed?.orderBy('reversedAt').reverse().toArray(),
          db.products?.toArray(),
        ]);

        const productsMap = (products || []).reduce((acc, p) => {
          acc[p.id] = p.name;
          return acc;
        }, {});

        console.log(
          '[OrdersReversed] Loaded reversed orders count:',
          reversedData ? reversedData.length : 0,
          'products count:',
          products ? products.length : 0
        );

        setProductsById(productsMap);
        setOrders(reversedData || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error loading reversed orders:', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const grandTotal = orders.reduce((sum, o) => sum + (o.total || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Cargando órdenes reversadas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-32">
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-white/20 rounded-lg text-white transition-colors flex items-center gap-1"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <RotateCcw size={20} /> Órdenes reversadas
            </h1>
            <p className="text-sm text-white/80">
              Histórico local de órdenes pendientes que fueron reversadas manualmente.
            </p>
          </div>
          <div className="p-2 bg-white/20 rounded-lg">
            <ShoppingCart size={20} className="text-white" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3 max-w-2xl mx-auto">
        {orders.length === 0 ? (
          <div className="card p-8 text-center">
            <RotateCcw size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No hay órdenes reversadas registradas.</p>
          </div>
        ) : (
          <>
            {orders.map((order) => (
            <div key={order.id} className="card p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 pr-4">
                  <p className="text-white font-semibold text-sm">
                    #{order.uuid?.slice(0, 8)}
                  </p>
                  <p className="text-gray-400 text-xs">
                    Reversada: {new Date(order.reversedAt).toLocaleString('es-ES', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </p>
                  <p className="text-gray-300 text-xs mt-1">
                    Motivo: {order.reason}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-primary-400 font-bold text-lg">
                    ${order.total?.toFixed(2)}
                  </p>
                </div>
              </div>

              {order.itemsSnapshot && order.itemsSnapshot.length > 0 && (
                <div className="border-t border-gray-800 mt-3 pt-3">
                  <p className="text-xs text-gray-400 mb-2">Productos en la orden:</p>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {order.itemsSnapshot.map((item, idx) => {
                      const productName = productsById[item.productId] || `Producto ${item.productId}`;
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-xs text-gray-300"
                        >
                          <span>
                            {productName} · Cant: {item.quantity}
                          </span>
                          <span>
                            ${item.price.toFixed(2)} c/u · {(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="mt-2 card p-3 flex items-center justify-between text-xs">
            <span className="text-gray-400">Total monto órdenes reversadas</span>
            <span className="font-semibold text-red-400 text-xl drop-shadow-[0_0_6px_rgba(248,113,113,0.6)]">
              ${grandTotal.toFixed(2)}
            </span>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersReversed;
